import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { DocumentType } from '@backrs_ui/grow-ui/src/models/models';

export type DocumentExtension = 'pdf' | 'docx' | 'txt' | 'image';

type UploadDocumentParams = Readonly<{
  documentType: DocumentType;
  isCoach?: boolean;
  existingFileName?: string;
  existingFileUrl?: string;
}>;

type MimeTypeConfig = Readonly<{
  type: DocumentExtension;
  extension: string;
  mimeTypes: string[];
}>;

const { fileUpload } = appStrings.modules.chatBox;

export const getUploadDocumentMessage = ({
  documentType,
  isCoach,
  existingFileName,
  existingFileUrl,
}: UploadDocumentParams) => {
  switch (documentType) {
    case 'resume':
      if (isCoach) {
        return fileUpload.askYourStudentToUploadResume;
      }

      if (existingFileName && existingFileUrl) {
        return fileUpload.uploadOrConfirmResume(existingFileName, existingFileUrl);
      }

      return fileUpload.uploadResume;
    case 'transcript':
      if (isCoach) {
        return fileUpload.askYourStudentToUploadTranscript;
      }

      if (existingFileName && existingFileUrl) {
        return fileUpload.uploadOrConfirmTranscript(existingFileName, existingFileUrl);
      }

      return fileUpload.uploadTranscript;
    default:
      return '';
  }
};

export const base64ToFile = ({
  base64,
  name,
  type,
}: {
  base64: string;
  name: string;
  type: string;
}): File => {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));

  return new File([bytes], name, { type });
};

export const FILE_CONFIG: Record<DocumentExtension, MimeTypeConfig> = {
  pdf: {
    type: 'pdf',
    extension: '.pdf',
    mimeTypes: ['application/pdf'],
  },
  docx: {
    type: 'docx',
    extension: '.docx',
    mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  },
  txt: {
    type: 'txt',
    extension: '.txt',
    mimeTypes: ['text/plain'],
  },
  image: {
    type: 'image',
    extension: '.jpg,.jpeg,.png',
    mimeTypes: ['image/jpeg', 'image/png'],
  },
} as const;

export const extractFileIdFromDriveLink = (link: string): string | null => {
  const patterns = [
    /\/file\/d\/([^/]+)/, // https://drive.google.com/file/d/FILE_ID/view
    /id=([^&]+)/, // https://drive.google.com/open?id=FILE_ID
    /\/d\/([^/]+)/, // https://drive.google.com/d/FILE_ID/view
  ];

  for (const pattern of patterns) {
    const match = link.match(pattern);

    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
};

export const getFileType = (mimeType: string): DocumentExtension => {
  return (
    Object.values(FILE_CONFIG).find((config) => config.mimeTypes.includes(mimeType))?.type ?? 'txt'
  );
};

export type FileUploadError = 'UNAUTHORIZED' | 'UNKNOWN_ERROR';

export const createTextFileFromString = (text: string, fileName: string): File => {
  const blob = new Blob([text], { type: 'text/plain' });

  return new File([blob], fileName, { type: 'text/plain' });
};
